// LocalTable — the LOCAL implementation of PlayTable: a thin adapter over the existing TableGame. It adds
// nothing to the game logic (so TableGame's 222 tests remain the behavioural source of truth); it only
// (a) redacts the full GameState to the seat-0 GameStateView via `view()` so the board reads the same
// shape as online play, and (b) notifies subscribers after each mutation so the board re-renders.
import type { GameStateView, Intent, RuleSet, Seat, TileId, YakuDefMeta, PlayerCount } from '@mj/protocol';
import { redactFor } from '@mj/engine';
import type { TableGame, EventJournal, HandResult, WaitKind } from './tableGame';
import type { PlayTable } from './playTable';

export class LocalTable implements PlayTable {
  readonly mode = 'local' as const;
  private listeners = new Set<() => void>();

  constructor(private readonly tg: TableGame) {}

  /** The wrapped controller, for LOCAL-only controls the play surface still drives directly. */
  get tableGame(): TableGame {
    return this.tg;
  }

  get players(): PlayerCount {
    return this.tg.players;
  }
  get humanSeat(): Seat {
    return this.tg.humanSeat;
  }
  get journal(): EventJournal {
    return this.tg.journal;
  }
  get kyoku(): number {
    return this.tg.kyoku;
  }
  get wait(): WaitKind {
    return this.tg.wait;
  }
  get result(): HandResult | null {
    return this.tg.result;
  }

  view(): GameStateView {
    return redactFor(this.tg.game, this.tg.humanSeat);
  }

  humanMoves(): Intent[] {
    return this.tg.humanMoves();
  }
  humanDrawn(): TileId | null {
    return this.tg.humanDrawn();
  }
  humanCanTsumo(): boolean {
    return this.tg.humanCanTsumo();
  }
  humanAnkanTiles(): TileId[] {
    return this.tg.humanAnkanTiles();
  }
  humanShouminkanTiles(): TileId[] {
    return this.tg.humanShouminkanTiles();
  }
  humanNukiTiles(): TileId[] {
    return this.tg.humanNukiTiles();
  }
  humanFlowerTiles(): TileId[] {
    return this.tg.humanFlowerTiles();
  }
  humanCanWindKan(): boolean {
    return this.tg.humanCanWindKan();
  }
  cpuTargetName(seat: Seat): string | null {
    return this.tg.cpuTargetName(seat);
  }

  act(intent: Intent): { ok: boolean; error?: string } {
    const r = this.tg.act(intent);
    this.notify();
    return r;
  }
  pass(): void {
    this.tg.pass();
    this.notify();
  }
  nextHand(): void {
    this.tg.nextHand();
    this.notify();
  }

  setSkipCalls(b: boolean): void {
    this.tg.setSkipCalls(b);
    this.notify();
  }
  setAutoWin(b: boolean): void {
    this.tg.setAutoWin(b);
    this.notify();
  }
  setAutoNuki(b: boolean): void {
    this.tg.setAutoNuki(b);
    this.notify();
  }

  setCpuPolicy(p: string): void {
    this.tg.setCpuPolicy(p);
    this.notify();
  }
  applyRules(rs: RuleSet, catalog: YakuDefMeta[]): void {
    this.tg.applyRules(rs, catalog);
    this.notify();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  dispose(): void {
    this.listeners.clear();
  }

  private notify(): void {
    for (const l of this.listeners) l();
  }
}
